/**
 * Making a league, for the site's admins and draft moderators, with the settings of the bot's /setup.
 * "Compatibility with bot" on: the bot runs it on Discord (in the server and channels chosen here), with the site as
 * a second way in. Off: it lives only on this site, still with the bot's rules (queues, timers, skips, trades).
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { api, signInUrl } from '../api';
import { useMe } from '../me';
import { navigate } from '../router';
import { ErrorBox, Loading, toast } from '../ui';

interface Guild {
	id: string;
	name: string;
	channels: { id: string, name: string, category: string }[];
}
interface Account {
	discordId: string;
	name: string;
	handle: string | null;
	showdown: string | null;
}
interface Pool {
	channelId: string;
	shuffle: boolean;
	players: Account[];
}

const TIMER_UNITS = { minutes: 60, hours: 3600 } as const;

function Field({ label, children, hint }: { label: string, children: preact.ComponentChildren, hint?: string }) {
	return <label class="field">{label}{children}{hint && <span class="small muted">{hint}</span>}</label>;
}

function ChannelSelect({ guild, value, onChange, placeholder }: {
	guild: Guild | undefined, value: string, onChange: (id: string) => void, placeholder: string,
}) {
	return <select value={value} onChange={e => onChange((e.target as HTMLSelectElement).value)}>
		<option value="">{placeholder}</option>
		{guild?.channels.map(c => <option key={c.id} value={c.id}>#{c.name}{c.category && ` (${c.category})`}</option>)}
	</select>;
}

/** One pool's players, in draft order, chosen from everyone who has signed in with Discord */
function PoolPlayers({ label, pool, accounts, taken, onChange }: {
	label: string, pool: Pool, accounts: Account[], taken: Set<string>, onChange: (pool: Pool) => void,
}) {
	const [search, setSearch] = useState('');
	const query = search.trim().toLowerCase();
	const matches = query ? accounts.filter(a => !taken.has(a.discordId) &&
		[a.name, a.handle || '', a.showdown || ''].some(text => text.toLowerCase().includes(query))).slice(0, 8) : [];
	const move = (from: number, to: number) => {
		const players = [...pool.players];
		players.splice(to, 0, ...players.splice(from, 1));
		onChange({ ...pool, players });
	};
	return <div>
		<h4 style="margin:12px 0 4px">Pool {label} players{pool.players.length > 0 && ` (${pool.players.length})`}</h4>
		<ol class="queue">
			{pool.players.map((p, i) => <li key={p.discordId} class="row">
				<span style="flex:1">{p.name}{p.handle && <span class="muted small"> @{p.handle}</span>}</span>
				<button type="button" class="small" disabled={i === 0} onClick={() => move(i, i - 1)}>↑</button>
				<button type="button" class="small" disabled={i === pool.players.length - 1} onClick={() => move(i, i + 1)}>↓</button>
				<button type="button" class="small" onClick={() => onChange({ ...pool, players: pool.players.filter(x => x !== p) })}>✕</button>
			</li>)}
		</ol>
		<input type="search" placeholder="Add a player: search by Discord or Showdown name" value={search} style="width:100%;max-width:420px"
			onInput={e => setSearch((e.target as HTMLInputElement).value)} />
		{matches.length > 0 && <div class="row" style="margin-top:6px">{matches.map(a => <button key={a.discordId} type="button" class="small"
			onClick={() => {
				onChange({ ...pool, players: [...pool.players, a] });
				setSearch('');
			}}>+ {a.name}{a.showdown && ` (${a.showdown})`}</button>)}</div>}
		{query && !matches.length && <p class="small muted">Nobody by that name has signed in to the site with Discord yet.</p>}
		<label class="row small" style="margin-top:6px"><input type="checkbox" checked={pool.shuffle}
			onChange={e => onChange({ ...pool, shuffle: (e.target as HTMLInputElement).checked })} /> Let the bot shuffle the draft order</label>
	</div>;
}

interface HostStanding {
	status: 'none' | 'pending' | 'approved' | 'rejected' | string;
	mayHost: boolean;
	needsDiscord: boolean;
}

/**
 * Hosting here is by approval, so somebody who hasn't been approved is given the form rather than a refusal.
 * The Discord bot asks nobody's permission, and that is said plainly: nobody is left stuck.
 */
function ApplyToHost({ standing, onApplied }: { standing: HostStanding, onApplied: () => void }) {
	const [about, setAbout] = useState('');
	const [sending, setSending] = useState(false);

	if (standing.needsDiscord) {
		return <div class="card">
			<h3 style="margin-top:0">Hosting needs your Discord account</h3>
			<p>A league is hosted by a Discord account, so link yours and then apply.</p>
		</div>;
	}

	const apply = async () => {
		setSending(true);
		try {
			await api.post('/api/hosts/apply', { about: about.trim() });
			toast(`Applied. You'll hear back — the Discord bot works in the meantime.`, 'good');
			onApplied();
		} catch (e) {
			toast((e as Error).message, 'bad');
		} finally {
			setSending(false);
		}
	};

	return <div class="card">
		<h3 style="margin-top:0">Apply to host on draft.ogreclan.com</h3>
		{standing.status === 'pending'
			? <p>⏳ <strong>Your application is being looked at.</strong> You'll be able to make a league here once
				it's approved, and you'll get a Discord message either way.</p>
			: <>
				{standing.status === 'rejected' && <p class="notice">Your last application wasn't approved. You can
					apply again — say what's changed.</p>}
				<p>A league published here goes out under Ogre Clan's name, so hosts are approved first. Tell us what
					you'd like to run.</p>
				<label class="field">What you want to host
					<textarea rows={4} value={about} maxLength={1000} placeholder="The format, how many coaches, when you'd like to start…"
						onInput={e => setAbout((e.target as HTMLTextAreaElement).value)} />
				</label>
				<button type="button" disabled={sending} onClick={() => void apply()}>
					{sending ? 'Applying…' : 'Apply to host'}
				</button>
			</>}
		<p class="small muted" style="margin-bottom:0">
			<strong>You don't need this to run a league on Discord.</strong> Invite the bot to your own server and
			run <code>/setup</code> — no application, no approval, all the same rules. This is only for publishing a
			league on this site.
		</p>
	</div>;
}

export function NewLeaguePage() {
	const me = useMe();
	const staff = !!(me?.user?.isAdmin || me?.user?.isModerator);
	const [accounts, setAccounts] = useState<Account[] | null>(null);
	const [guilds, setGuilds] = useState<Guild[] | null>(null);
	const [error, setError] = useState<unknown>(null);
	const [standing, setStanding] = useState<HostStanding | null>(null);
	const [onDiscord, setOnDiscord] = useState(true);
	const [form, setForm] = useState({
		name: '', mode: 'full_season', budget: '100', teraBudget: '0', minPicks: '8', maxPicks: '10', timer: '1', timerUnit: 'hours' as keyof typeof TIMER_UNITS,
		maxTrades: '0', weeks: '', private: false, website: true, document: 'none', link: '', guildId: '', adminChannelId: '', twoPools: false,
	});
	const [pools, setPools] = useState<Pool[]>([{ channelId: '', shuffle: false, players: [] }, { channelId: '', shuffle: false, players: [] }]);
	const set = (changes: Partial<typeof form>) => setForm({ ...form, ...changes });

	const loadStanding = () => {
		api.get<HostStanding>('/api/hosts/me').then(setStanding,
			// an older bot, or one that isn't answering: don't block the form over it
			() => setStanding({ status: staff ? 'approved' : 'none', mayHost: staff, needsDiscord: false }));
	};

	// anyone signed in can apply to host; once approved, they make leagues that run on the site
	useEffect(() => {
		if (me?.user) loadStanding();
	}, [!!me?.user]);
	useEffect(() => {
		if (!standing?.mayHost) return;
		api.get<{ accounts: Account[] }>('/api/bot-setup/accounts').then(r => setAccounts(r.accounts), setError);
		if (!staff) {
			setOnDiscord(false);
			return;
		}
		api.get<{ guilds: Guild[] }>('/api/bot-setup/guilds').then(r => {
			setGuilds(r.guilds);
			if (r.guilds.length === 1) setForm(f => ({ ...f, guildId: r.guilds[0]!.id }));
		}, () => setGuilds([]));
	}, [standing?.mayHost, staff]);

	const guild = guilds?.find(g => g.id === form.guildId);
	const used = pools.slice(0, form.twoPools ? 2 : 1);
	const taken = useMemo(() => new Set(used.flatMap(p => p.players.map(x => x.discordId))), [pools, form.twoPools]);

	if (!me) return <Loading />;
	if (!me.user) {
		return <>
			<h1>Host a league</h1>
			<p class="notice">Sign in with Discord to apply to host a league on draft.ogreclan.com.{' '}
				<a class="button primary small" href={signInUrl()}>Sign in with Discord</a></p>
		</>;
	}
	if (error) return <ErrorBox error={error} />;
	if (!standing) return <Loading />;
	if (!standing.mayHost) {
		return <>
			<h1>{staff ? 'Make a league' : 'Host a league'}</h1>
			<ApplyToHost standing={standing} onApplied={loadStanding} />
		</>;
	}
	if (!accounts) return <Loading />;

	const submit = async () => {
		const number = (text: string) => text.trim() === '' ? null : Number(text);
		try {
			const result = await api.post<{ league_id: number, notes: string[] }>('/api/bot-leagues', {
				siteOnly: !onDiscord, guildId: form.guildId, adminChannelId: form.adminChannelId, name: form.name.trim(), mode: form.mode,
				budget: number(form.budget), teraBudget: number(form.teraBudget), minPicks: number(form.minPicks), maxPicks: number(form.maxPicks),
				timer: Math.round(Number(form.timer) * TIMER_UNITS[form.timerUnit]), maxTrades: number(form.maxTrades), weeks: number(form.weeks),
				private: form.private, website: form.website,
				templateLink: form.document === 'template' ? form.link.trim() : null, tieringLink: form.document === 'tiering' ? form.link.trim() : null,
				pools: used.map(p => ({ channelId: p.channelId, shuffle: p.shuffle, players: p.players.map(x => ({ discordId: x.discordId, name: x.name })) })),
			});
			toast(`${form.name.trim()} is made.${result.notes.length ? ` ${result.notes.join(' ')}` : ''}`, 'good');
			// the site sees the bot's leagues a couple of seconds after they change
			setTimeout(() => navigate(`/discord/${result.league_id}`), 3000);
		} catch (e) {
			toast((e as Error).message, 'bad');
		}
	};

	return <>
		<h1>{staff ? 'Make a league' : 'Host a league'}</h1>
		<p class="muted">The same settings as the bot's <code>/setup</code>. Players need to have signed in to this site with Discord once.</p>
		<form class="stack" onSubmit={e => {
			e.preventDefault();
			void submit();
		}}>
			{!staff && <div class="card">
				<h3 style="margin-top:0">Where it runs</h3>
				<p style="margin:0">It runs on draft.ogreclan.com, with the bot's rules (queues, timers, skips, trades), and you run it from
					its page. For a league run on Discord, invite the bot to your own server and use <code>/setup</code> there: it shows
					up on this site too.</p>
			</div>}
			{staff && <div class="card">
				<h3 style="margin-top:0">Where it runs</h3>
				<label class="row"><input type="radio" name="where" checked={onDiscord} onChange={() => setOnDiscord(true)} />
					<span><strong>Compatibility with bot:</strong> the bot runs it on Discord (picks, trades, announcements), and the site is a second way in</span></label>
				<label class="row" style="margin-top:6px"><input type="radio" name="where" checked={!onDiscord} onChange={() => setOnDiscord(false)} />
					<span><strong>Only on draft.ogreclan.com:</strong> the bot's rules (queues, timers, skips, trades), with no Discord server or channels</span></label>
				{onDiscord && <div class="grid" style="margin-top:10px">
					<Field label="Discord server">
						<select value={form.guildId} required onChange={e => set({ guildId: (e.target as HTMLSelectElement).value, adminChannelId: '' })}>
							<option value="">Choose…</option>
							{guilds?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
						</select>
					</Field>
					<Field label="Trade approvals channel" hint="Where trades and substitution requests go (the channel /setup is run in)">
						<ChannelSelect guild={guild} value={form.adminChannelId} placeholder="Choose…" onChange={id => set({ adminChannelId: id })} />
					</Field>
					<label class="row small"><input type="checkbox" checked={form.website} onChange={e => set({ website: (e.target as HTMLInputElement).checked })} />
						Compatibility with website: picks and queues on the site, matches with Ready on play.ogreclan.com, kills and deaths recorded</label>
				</div>}
			</div>}

			<div class="card">
				<h3 style="margin-top:0">Settings</h3>
				<div class="grid">
					<Field label="Name"><input value={form.name} required maxLength={80} onInput={e => set({ name: (e.target as HTMLInputElement).value })} /></Field>
					<Field label="Mode">
						<select value={form.mode} onChange={e => set({ mode: (e.target as HTMLSelectElement).value })}>
							<option value="full_season">Full season (draft, trades and schedule)</option>
							<option value="draft_schedule">Draft and schedule (no trades)</option>
							<option value="draft_only">Draft only (archived once the draft ends)</option>
						</select>
					</Field>
					<Field label="Budget (points)" hint={form.document === 'template' ? 'Read from the template' : undefined}>
						<input type="number" min="1" value={form.budget} disabled={form.document === 'template'} onInput={e => set({ budget: (e.target as HTMLInputElement).value })} />
					</Field>
					<Field label="Tera budget" hint="0 for no Tera Captains">
						<input type="number" min="0" value={form.teraBudget} disabled={form.document === 'template'} onInput={e => set({ teraBudget: (e.target as HTMLInputElement).value })} />
					</Field>
					<Field label="Fewest Pokémon each"><input type="number" min="1" value={form.minPicks} required onInput={e => set({ minPicks: (e.target as HTMLInputElement).value })} /></Field>
					<Field label="Most Pokémon each" hint="The number of rounds"><input type="number" min="1" value={form.maxPicks} required onInput={e => set({ maxPicks: (e.target as HTMLInputElement).value })} /></Field>
					<Field label="Pick timer">
						<div class="row">
							<input type="number" min="0.5" step="0.5" value={form.timer} required style="width:90px" onInput={e => set({ timer: (e.target as HTMLInputElement).value })} />
							<select value={form.timerUnit} onChange={e => set({ timerUnit: (e.target as HTMLSelectElement).value as keyof typeof TIMER_UNITS })}>
								<option value="minutes">minutes</option><option value="hours">hours</option>
							</select>
						</div>
					</Field>
					{form.mode === 'full_season' && <Field label="Trade pickups each" hint="Pokémon each player may pick up in trades; 0 for no limit">
						<input type="number" min="0" value={form.maxTrades} onInput={e => set({ maxTrades: (e.target as HTMLInputElement).value })} />
					</Field>}
					{form.mode !== 'draft_only' && form.document !== 'template' && <Field label="Weeks" hint="For the schedule the bot makes">
						<input type="number" min="1" value={form.weeks} onInput={e => set({ weeks: (e.target as HTMLInputElement).value })} />
					</Field>}
					<Field label="Who can see it on the site">
						<select value={form.private ? 'private' : 'public'} onChange={e => set({ private: (e.target as HTMLSelectElement).value === 'private' })}>
							<option value="public">Everyone</option><option value="private">Only its players (private)</option>
						</select>
					</Field>
				</div>
			</div>

			<div class="card">
				<h3 style="margin-top:0">Spreadsheet</h3>
				<div class="grid">
					<Field label="Document">
						<select value={form.document} onChange={e => set({ document: (e.target as HTMLSelectElement).value, twoPools: (e.target as HTMLSelectElement).value === 'template' && form.twoPools })}>
							<option value="none">None</option>
							<option value="template">Full league template (rosters, settings and schedule)</option>
							<option value="tiering">Tiering sheet (names, prices, Tera prices)</option>
						</select>
					</Field>
					{form.document !== 'none' && <Field label="Its link"><input type="url" value={form.link} required placeholder="https://docs.google.com/spreadsheets/d/…"
						onInput={e => set({ link: (e.target as HTMLInputElement).value })} /></Field>}
				</div>
			</div>

			<div class="card">
				<h3 style="margin-top:0">Pools and players</h3>
				{form.document === 'template' && <label class="row small"><input type="checkbox" checked={form.twoPools}
					onChange={e => set({ twoPools: (e.target as HTMLInputElement).checked })} /> Two pools (pool B as well)</label>}
				{used.map((pool, i) => <div key={i}>
					{onDiscord && <div style="max-width:360px;margin-top:10px"><Field label={`Pool ${'AB'[i]}'s draft channel`}>
						<ChannelSelect guild={guild} value={pool.channelId} placeholder="Choose…" onChange={id => setPools(pools.map((p, j) => j === i ? { ...p, channelId: id } : p))} />
					</Field></div>}
					<PoolPlayers label={'AB'[i]!} pool={pool} accounts={accounts} taken={taken}
						onChange={next => setPools(pools.map((p, j) => j === i ? next : p))} />
				</div>)}
			</div>

			<div><button type="submit" class="primary" disabled={!form.name.trim() || used.some(p => p.players.length < 2)}>Make the league</button></div>
		</form>
	</>;
}
